import React, { useMemo } from 'react';
import { PostInputUI } from '@components/Feed/PostInputUI';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { ModalType, useModal } from '@context/modal';
import { useStableCallback } from '@hooks/useStableCallback';
import { trackClickEvent } from '@lib/analytics';
import { usePostToChannel } from '../usePostToChannel';

export function ChannelPostInput({ channelId }: { channelId: string }) {
  const { authUser, isAuthenticatedWithAPI } = useAuth();
  const { openModal } = useModal();

  const { isAllowedToPost } = usePostToChannel({ channelId });

  const inputPlaceholder = useMemo(() => {
    if (isAuthenticatedWithAPI) {
      return `What sound is on your mind${
        authUser?.displayName ? `, ${authUser.displayName}...` : '?'
      }`;
    } else {
      return 'Sign in to post in channel...';
    }
  }, [isAuthenticatedWithAPI, authUser?.displayName]);

  const onClick = useStableCallback(() => {
    if (!isAuthenticatedWithAPI) {
      triggerLogin();
      return;
    }

    if (!isAllowedToPost) {
      return;
    }

    trackClickEvent({
      event: 'Create Post Clicked',
      properties: {
        sourceComponent: 'Channel Post Input',
      },
    });
    openModal(ModalType.CHANNEL_POST, {
      body: (
        <FeedPostModal
          key="post-input"
          sourceLocation="Channel Post Input"
          preselectedResource={null}
          channelId={channelId}
        />
      ),
    });
  });

  if (!isAllowedToPost) {
    return null;
  }

  return (
    <div className="mt-10">
      <PostInputUI
        onClick={onClick}
        placeholder={inputPlaceholder}
        isLocked={!isAllowedToPost}
        type="feed"
        as="default"
      />
    </div>
  );
}
